import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { HorizontalFlex, PageIcon, VerticalFlex, ViewContainer } from "./styled";
import { Background } from "../public/styled";
import SecurityIcon from "../../assets/svg/SecurityIcon.svg";
import projectIcon from "../../assets/svg/projectIcon.svg";
import profilepersonIcon from "../../assets/svg/profilepersonIcon.svg";
import openLock from "../../assets/svg/openLock.svg";
import nameBadge from "../../assets/svg/nameBadge.svg";
import addUserIcon from "../../assets/svg/addUserIcon.svg";
import AddUserInput from "../../components/profileComponents/AddUserInput";
import { Timeout } from "../public/Login";
import { IUser, INewUser, IGroup, IProject } from "../../api/Interfaces";
import Sidebar from "../../components/Sidebar/Sidebar";
import Group from "./Group";

const ContentContainer = styled(VerticalFlex)`
  position: absolute;
  height: auto;
  width: 50em;
  padding: 30px;
  margin: 20px auto;
  border-radius: 15px;
  background-color: rgba(50, 54, 68, 1);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  top: 5em;
  left: 50%;
  margin-left: 10px;

  @media (max-width: 1650px) {
    width: 45em;
    padding: 25px;
  }

  @media (max-width: 1400px) {
    width: 40em;
    padding: 20px;
  }

  @media (max-width: 1200px) {
    position: relative;
    width: 100%;
    max-width: 800px;
    min-width: 320px;
    top: auto;
    left: auto;
    margin: 20px auto;
    margin-left: auto;
  }

  @media (max-width: 768px) {
    max-width: 95%;
    padding: 15px;
  }
`;

const AddUserButton = styled.button`
  background-color: #198b74;
  color: #fff;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  width: 180px;
  height: auto;
  min-width: 140px;

  &:hover {
    background-color: rgba(23, 167, 138, 0.65);
  }
  &:focus {
    outline: none;
  }

  @media (max-width: 1650px) {
    width: 160px;
    font-size: 18px;
    padding: 8px 16px;
  }

  @media (max-width: 1400px) {
    width: 140px;
    font-size: 16px;
    padding: 8px 12px;
  }

  @media (max-width: 768px) {
    width: 120px;
    font-size: 14px;
    padding: 6px 10px;
  }
`;

const PageTitles = styled("div")`
  font-size: 20px;
  color: white;

  @media (max-width: 1650px) {
    font-size: 18px;
  }

  @media (max-width: 1400px) {
    font-size: 16px;
  }

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const AddUserIconStyled = styled(PageIcon)`
  margin-right: 20px;
  width: 30px;
  height: 30px;

  @media (max-width: 1650px) {
    width: 25px;
    height: 25px;
    margin-right: 15px;
  }

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }
`;

const ResponsiveWrapper = styled.div`
  width: 100%;
  margin: 0 auto;

  @media (max-width: 1200px) {
    max-width: 1200px;
    padding: 0 20px;
  }

  @media (max-width: 1000px) {
    max-width: 1000px;
    padding: 0 15px;
  }

  @media (max-width: 768px) {
    padding: 0 10px;
  }
`;

const Title = styled("h1")<{ size: number; css?: string }>`
  margin: 0;
  margin-top: 5px;
  text-align: left;
  font-size: ${(props) => props.size}px;
  font-family: "Montserrat";
  font-weight: bold;
  color: white;
  word-break: break-all;
  ${(props) => props.css ?? ""};

  @media (max-width: 1650px) {
    font-size: ${(props) => Math.max(props.size * 0.9, 12)}px;
  }

  @media (max-width: 1400px) {
    font-size: ${(props) => Math.max(props.size * 0.8, 11)}px;
  }

  @media (max-width: 768px) {
    font-size: ${(props) => Math.max(props.size * 0.7, 10)}px;
    word-break: break-word;
  }
`;

interface ProfileViewProps {
  currentUser: IUser;
}

const Profile = (props: ProfileViewProps): React.ReactElement => {
  const { currentUser: initialCurrentUser } = props;
  const API_URL: string | undefined = process.env.REACT_APP_API_URL;

  const [currentUser, setCurrentUser] = useState<IUser>(initialCurrentUser);
  const [users, setUsers] = useState<IUser[]>([]);
  const [projects, setProjects] = useState<IProject[]>([]);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [showAddUserPopup, setShowAddUserPopup] = useState<boolean>(false);
  const [addGroup, setAddGroup] = useState<IGroup | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState(currentUser?.full_name || "");
  const [email, setEmail] = useState(currentUser?.email || "");
  const [activeSection, setActiveSection] = useState<string | null>("content");
  const [isActive, setIsActive] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordMessage, setPasswordMessage] = useState<string>("");
  const [passwordMessageType, setPasswordMessageType] = useState<"success" | "error" | "">("");
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth <= 1650);

  document.title = `My profile | Web360`;

  useEffect(() => {
    getUsers();
    getProjects();
    fetchGroups();

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 1650);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [API_URL]);

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API_URL}/profile/accessible_groups`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (res.ok) {
        const data = await res.json();
        setGroups(data);
      } else if (res.status === 401) {
        setError("Not authenticated. Please log in again.");
      } else {
        console.error("Failed to fetch groups:", res.statusText);
        setError("Failed to load groups. Please try again later.");
      }
    } catch (err) {
      console.error("Error fetching groups:", err);
      setError("An error occurred while loading groups.");
    }
  };

  const getUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/profile/group_users`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        signal: Timeout(7).signal,
      });
      if (res.ok) {
        const body = await res.json();
        setUsers(body);
        setError(null);
      } else if (res.status === 401) {
        setError("Not authenticated. Please log in again.");
      } else {
        console.error("Failed to fetch group users:", res.statusText);
        setUsers([]);
        setError("Failed to load group users. Please try again later.");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setUsers([]);
      setError("An error occurred while loading group users.");
    }
  };

  const addUser = async (newUser: INewUser, setLoading: (loading: boolean) => void) => {
    try {
        setLoading(true);
        const res = await fetch(`${API_URL}/profile/add_user_to_group`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newUser),
        });
        if (res.ok) {
            const data = await res.json();
            if (data.email_warning) 
            await getUsers();
        } else {
            const errorData = await res.json();
            throw new Error(errorData.detail || res.statusText);
        }
    } catch (err) {
        console.error("Error adding user:", err);
        alert(`Failed to add user: ${err instanceof Error ? err.message : "Unknown error"}`);
        throw err;
    } finally {
        setLoading(false);
    }
  };

  const getProjects = async () => {
    try {
      const res = await fetch(`${API_URL}/projects`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        signal: Timeout(7).signal,
      });
      if (res.ok) {
        const body = await res.json();
        const adminToGroups = currentUser?.admin_of_groups || [];
        if (adminToGroups.length > 0) {
          const adminProjectIds = new Set(
            adminToGroups.flatMap((group) => group.projects.map((project) => project.id))
          );
          const filteredProjects = body.filter((project) => adminProjectIds.has(project.id));
          setProjects(filteredProjects);
        } else {
          setProjects(body);
        }
      } else {
        console.error("Failed to fetch projects:", res.statusText);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage("");
    setPasswordMessageType("");

    if (!newPassword || !confirmPassword) {
      setPasswordMessage("Password can't be empty!");
      setPasswordMessageType("error");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage("Passwords do not match!");
      setPasswordMessageType("error");
      return;
    }
    try {
      const response = await fetch(`${API_URL}/users/me`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: newPassword }),
        signal: Timeout(7).signal,
      });
      if (response.ok) {
        setPasswordMessage("Password Updated!");
        setPasswordMessageType("success");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setPasswordMessage("Failed to update password!");
        setPasswordMessageType("error");
      }
    } catch (err) {
      console.error("Error updating password:", err);
      setPasswordMessage("An error occurred. Please try again later.");
      setPasswordMessageType("error");
    }
  };

  const toggleContent = () => setActiveSection(activeSection === "content" ? null : "content");
  const toggleSecurityContent = () => { setActiveSection(activeSection === "security" ? null : "security"); setIsActive(!isActive); };
  const toggleProjectContent = () => setActiveSection(activeSection === "project" ? null : "project");

  return (
    <>
      {showAddUserPopup && (
        <AddUserInput
          addUser={addUser}
          title="Add new user"
          close={() => setShowAddUserPopup(false)}
          group={addGroup}
          isSuperuser={currentUser.is_superuser}
          currentUser={currentUser} admin_of_groups={false}
      />
      )}

      <Sidebar page="Profile" />
      <Background>
        <ViewContainer>
          <ResponsiveWrapper>
          <HorizontalFlex
            css={`
              align-items: center;
              margin-top: 5em;
              margin-bottom: 20px;
              padding: 15px;
              border-bottom: 2px solid rgb(44, 33, 33);
              background-color: ${activeSection === "content" ? "#5485F8" : "rgba(50, 54, 68, 1)"};
              max-width: 15%;
              margin-left: 18em;
              margin-right: auto;
              position: relative;
              cursor: pointer;
              transition: background-color 0.3s ease;
              border-radius: 10px;
                &:hover {
                  background-color: #5485F8;
                }

              @media (max-width: 1650px) {
                margin-left: 15em;
                max-width: 18%;
              }

              @media (max-width: 1400px) {
                margin-left: 12em;
                max-width: 22%;
              }

              @media (max-width: 1200px) {
                margin-left: auto;
                margin-right: auto;
                max-width: 300px;
                width: 100%;
              }

              @media (max-width: 768px) {
                margin-left: auto;
                margin-right: auto;
                max-width: 90%;
                padding: 12px;
              }
            `}
          >
            <PageIcon icon={profilepersonIcon} onClick={toggleContent} />
            <PageTitles onClick={toggleContent}>Profile</PageTitles>
          </HorizontalFlex>
          {activeSection === "content" && (
            <ContentContainer>
              <form>
                <Title
                  size={21}
                  css={`
                    font-weight: extra bold;
                    margin-bottom: 30px;
                    background-image: url(${nameBadge});
                    background-size: 35px 35px;
                    background-repeat: no-repeat;
                    background-position: left center;
                    padding-left: 50px;
                  `}
                >
                  {currentUser.full_name}
                </Title>
                <HorizontalFlex css="justify-content: space-between; margin-bottom: 20px;">
                  <VerticalFlex css="width: 100%;">
                    <Title size={16}>Full Name</Title>
                    <input
                      placeholder="Full Name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled
                      style={{
                        marginTop: "10px",
                        padding: "10px",
                        width: "100%",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        backgroundColor: "#272A37",
                        color: "#fff",
                        fontSize: "16px",
                        boxSizing: "border-box",
                        backgroundImage: `url(${nameBadge})`,
                        backgroundSize: "20px 20px",
                        backgroundPosition: "right 10px center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  </VerticalFlex>
                </HorizontalFlex>
                <HorizontalFlex css="justify-content: space-between; margin-bottom: 20px;">
                  <VerticalFlex css="width: 100%;">
                    <Title size={16}>Email</Title>
                    <input
                      placeholder="Email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled
                      style={{
                        marginTop: "10px",
                        padding: "10px",
                        width: "100%",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        backgroundColor: "#272A37",
                        color: "#fff",
                        fontSize: "16px",
                        boxSizing: "border-box",
                        backgroundImage: `url(${nameBadge})`,
                        backgroundSize: "20px 20px",
                        backgroundPosition: "right 10px center",
                        backgroundRepeat: "no-repeat",
                      }}
                    />
                  </VerticalFlex>
                </HorizontalFlex>
              </form>
            </ContentContainer>
          )}

          <HorizontalFlex
            css={`
              align-items: center;
              margin-top: 2em;
              margin-bottom: 20px;
              padding: 15px;
              border-bottom: 2px solid rgb(44, 33, 33);
              background-color: ${activeSection === "security" ? "#5485F8" : "rgba(50, 54, 68, 1)"};
              max-width: 15%;
              margin-left: 18em;
              margin-right: auto;
              position: relative;
              cursor: pointer;
              transition: background-color 0.3s ease;
              border-radius: 10px;
                &:hover {
                  background-color: #5485F8;
                }

              @media (max-width: 1650px) {
                margin-left: 15em;
                max-width: 18%;
              }

              @media (max-width: 1400px) {
                margin-left: 12em;
                max-width: 22%;
              }

              @media (max-width: 1200px) {
                margin-left: auto;
                margin-right: auto;
                max-width: 300px;
                width: 100%;
              }

              @media (max-width: 768px) {
                margin-left: auto;
                margin-right: auto;
                max-width: 90%;
                padding: 12px;
              }
            `}
          >
            <PageIcon icon={SecurityIcon} onClick={toggleSecurityContent} />
            <PageTitles onClick={toggleSecurityContent}>Password</PageTitles>
          </HorizontalFlex>
          {activeSection === "security" && (
            <ContentContainer>
              <form onSubmit={handlePasswordChange} style={{ width: "100%" }}>
                <Title
                  size={20}
                  css={`
                    margin-bottom: 30px;
                    background-image: url(${SecurityIcon});
                    background-size: 25px 25px;
                    background-repeat: no-repeat;
                    background-position: left center;
                    padding-left: 50px;
                    @media (max-width: 1650px) {
                      margin-bottom: 20px;
                    }
                  `}
                >
                  Password
                </Title>
                <div style={{ 
                  marginBottom: isSmallScreen ? "20px" : "50px", 
                  marginTop: isSmallScreen ? "20px" : "60px"
                }}>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New Password"
                    style={{
                      width: "100%",
                      padding: isSmallScreen ? "12px" : "15px",
                      borderRadius: "12px",
                      border: "2px solid #ccc",
                      backgroundColor: "#272A37",
                      color: "white",
                      fontSize: "16px",
                      boxSizing: "border-box",
                      backgroundImage: `url(${openLock})`,
                      backgroundSize: "20px 20px",
                      backgroundPosition: "right 10px center",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{
                      width: "100%",
                      padding: isSmallScreen ? "12px" : "15px",
                      borderRadius: "12px",
                      border: "2px solid #ccc",
                      backgroundColor: "#272A37",
                      color: "white",
                      fontSize: "16px",
                      boxSizing: "border-box",
                      backgroundImage: `url(${openLock})`,
                      backgroundSize: "20px 20px",
                      backgroundPosition: "right 10px center",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                  {passwordMessage && (
                    <div style={{
                      marginTop: "8px",
                      fontSize: "14px",
                      color: passwordMessageType === "success" ? "#4CAF50" : "#f44336",
                      fontWeight: "500"
                    }}>
                      {passwordMessage}
                    </div>
                  )}
                </div>
                <div style={{ 
                  marginTop: isSmallScreen ? '30px' : '13em', 
                  display: 'flex', 
                  justifyContent: 'flex-start', 
                  width: '100%' 
                }}>
                 <button type="submit" style={{ 
                    backgroundColor: '#5485F8', 
                    color: '#fff', 
                    padding: '12px 30px', 
                    border: 'none', 
                    borderRadius: '12px', 
                    cursor: 'pointer', 
                    fontSize: '12px', 
                    fontWeight: '900', 
                    transition: 'background-color 0.3s ease',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    minWidth: '90px',
                    maxWidth: '200px' }} > 
                    Update Password </button> </div>
              </form>
            </ContentContainer>
             )}

            <HorizontalFlex
              css={`
                align-items: center;
                margin-top: 2em;
                margin-bottom: 20px;
                padding: 15px;
                border-bottom: 2px solid rgb(44, 33, 33);
                background-color: ${activeSection === "project" ? "#5485F8" : "rgba(50, 54, 68, 1)"};
                max-width: 15%;
                margin-left: 18em;
                margin-right: auto;
                position: relative;
                cursor: pointer;
                transition: background-color 0.3s ease;
                border-radius: 10px;
                &:hover {
                  background-color: #5485F8;
                }

                @media (max-width: 1650px) {
                  margin-left: 15em;
                  max-width: 18%;
                }

                @media (max-width: 1400px) {
                  margin-left: 12em;
                  max-width: 22%;
                }

                @media (max-width: 1200px) {
                  margin-left: auto;
                  margin-right: auto;
                  max-width: 300px;
                  width: 100%;
                }

                @media (max-width: 768px) {
                  margin-left: auto;
                  margin-right: auto;
                  max-width: 90%;
                  padding: 12px;
                }
              `}
            >
              <PageIcon icon={projectIcon} onClick={toggleProjectContent} />
              <PageTitles onClick={toggleProjectContent}>Organization</PageTitles>
            </HorizontalFlex>
            {activeSection === "project" && (
              <ContentContainer>
                {groups.length === 0 ? (
                  <Title size={20} css="text-align: center;">
                    No accessible groups found.
                  </Title>
                ) : (
                  <>
                    {currentUser.is_superuser ? (
                      <HorizontalFlex
                        css={`
                          justify-content: flex-end;
                          align-items: center;
                          width: 100%;
                          padding: 10px 15px;
                          background-color: rgba(60, 63, 77, 1);
                          border-radius: 10px;
                          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                          margin-bottom: 8px;
                          transition: transform 0.2s ease, background-color 0.3s ease;
                          &:hover {
                            transform: translateY(-2px);
                            background-color: rgba(70, 73, 87, 1);
                          }
                        `}
                      >
                        <AddUserButton
                          onClick={() => {
                            setAddGroup(null);
                            setShowAddUserPopup(true);
                          }}
                          style={{
                            backgroundColor: "rgba(23, 167, 138, 0.65)",
                            color: "#FFF",
                            padding: "15px 15px",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "20px",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            width: "200px",
                            height: "auto",
                            transition: "transform 0.3s ease, background-color 0.3s ease",
                          }}
                        >
                          <AddUserIconStyled icon={addUserIcon} />
                          Superuser
                        </AddUserButton>
                      </HorizontalFlex>
                    ) : (
                      <HorizontalFlex css="align-items: flex-start; flex-direction: column; gap: 15px;">
                        {groups.map((group) => (
                          <HorizontalFlex
                            key={group.id}
                            css={`
                              justify-content: space-between;
                              align-items: center;
                              width: 100%;
                              padding: 10px 15px;
                              background-color: rgba(60, 63, 77, 1);
                              border-radius: 10px;
                              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                              margin-bottom: 8px;
                              transition: transform 0.2s ease, background-color 0.3s ease;
                              &:hover {
                                transform: translateY(-2px);
                                background-color: rgba(70, 73, 87, 1);
                              }
                            `}
                          >
                            <Title
                              size={26}
                              css={`
                                margin: 0;
                                background-image: url(${projectIcon});
                                background-size: 35px 35px;
                                background-repeat: no-repeat;
                                background-position: left center;
                                padding-left: 40px;
                              `}
                            >
                              {group.name}
                            </Title>
                            {currentUser.admin_of_groups?.some((g) => g.id === group.id) && (
                              <AddUserButton
                                onClick={() => {
                                  setAddGroup(group);
                                  setShowAddUserPopup(true);
                                }}
                                style={{
                                  backgroundColor: "#198b74",
                                  color: "#fff",
                                  padding: "20px 10px",
                                  border: "none",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  fontSize: "18px",
                                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                  display: "flex",
                                  width: "180px",
                                  height: "50px",
                                  transition: "transform 0.3s ease, background-color 0.3s ease",
                                }}
                              >
                                <AddUserIconStyled icon={addUserIcon} />
                                Add User
                              </AddUserButton>
                            )}
                          </HorizontalFlex>
                        ))}
                      </HorizontalFlex>
                    )}
                    <HorizontalFlex css="align-items: center;">
                      <VerticalFlex>
                        {groups.map((group) => (
                          <Group
                            key={group.id}
                            group={group}
                            initialOpen={false}
                            showUsers={true}
                            users={users.filter(u => u.groups.some(g => g.id === group.id))}
                            isSuperuser={currentUser.is_superuser}
                          />
                        ))}
                      </VerticalFlex>
                    </HorizontalFlex>
                  </>
                )}
              </ContentContainer>
            )}
          </ResponsiveWrapper>
        </ViewContainer>
      </Background>
    </>
  );
};

export default Profile;